// webcam-overlay.service.ts
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { devAPI, prodAPI } from '../../../env';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface Camera {
  id: number;
  vdo_ninja_link: string | null;
  username: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class WebcamService {
  private apiUrl = `${prodAPI}/api/obs/cameras`;
  private camerasSubject = new BehaviorSubject<Camera[]>(this.getDefaultCameras());
  public cameras$ = this.camerasSubject.asObservable();
  private sanitizer = inject(DomSanitizer);
  private urlCache = new Map<string, SafeResourceUrl>();
  private currentUrls: Map<number, string> = new Map();
  private isUnmuted: boolean = false; // Флаг включения звука

  constructor(private http: HttpClient) {}

  // Метод для включения звука
  unmuteAll(): void {
    this.isUnmuted = true;
    this.urlCache.clear(); // Очищаем кэш чтобы пересоздать URL с звуком
  }

  getCachedSafeUrl(url: string | null): SafeResourceUrl {
    if (!url) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    }
    
    const cacheKey = `${url}_${this.isUnmuted}`; // Уникальный ключ с учетом состояния звука
    
    if (this.urlCache.has(cacheKey)) {
      return this.urlCache.get(cacheKey)!;
    }
    
    // Параметры в зависимости от состояния звука
    const volumeParam = this.isUnmuted ? '&volume=100' : '&volume=0';
    const finalUrl = `${url}&clean=true&autoplay=true&stretch=true&aspect=false&nocontrols=true${volumeParam}`;
    
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
    this.urlCache.set(cacheKey, safeUrl);
    
    return safeUrl;
  }

  loadCameras(): Observable<Camera[]> {
    return this.http.get<Camera[]>(this.apiUrl).pipe(
      map(response => response || this.getDefaultCameras()),
      tap(cameras => this.processCameras(cameras)),
      catchError(error => {
        console.error('Error loading cameras:', error);
        return of(this.getDefaultCameras());
      })
    );
  }

  startPolling(intervalMs: number = 5000): void {
    setInterval(() => {
      this.loadCameras().subscribe();
    }, intervalMs);
  }

  private processCameras(cameras: Camera[]): void {
    this.cleanupUrlCache(cameras);

    const processedCameras = cameras.map(camera => {
      const previousUrl = this.currentUrls.get(camera.id);
      const currentUrl = camera.vdo_ninja_link || null;
      
      if (currentUrl && currentUrl !== previousUrl) {
        this.currentUrls.set(camera.id, currentUrl);
      } else if (!currentUrl && previousUrl) {
        this.currentUrls.delete(camera.id);
      }

      const effectiveStreamUrl = currentUrl || previousUrl || null;
      
      return {
        ...camera,
        vdo_ninja_link: effectiveStreamUrl,
      };
    });
    const sortedCameras = processedCameras.sort((a, b) => a.id - b.id);
    this.camerasSubject.next(sortedCameras);
  }

  private cleanupUrlCache(cameras: Camera[]): void {
    const currentActiveUrls = new Set(
      cameras
        .map(cam => cam.vdo_ninja_link)
        .filter((url): url is string => url !== null)
    );

    this.urlCache.forEach((value, key) => {
      const url = key.split('_')[0]; // Извлекаем оригинальный URL из ключа
      if (!currentActiveUrls.has(url)) {
        this.urlCache.delete(key);
      }
    });
  }

  private getDefaultCameras(): Camera[] {
    return [
      { id: 1, username: null, vdo_ninja_link: null },
      { id: 2, username: null, vdo_ninja_link: null },
      { id: 3, username: null, vdo_ninja_link: null }
    ];
  }
}